import * as Yup from 'yup';
import { WEBSITE_REG } from 'constants/regularExpressions';
import { RelationshipModel } from 'modules/IntakeForm/EntityIntakeForm/IntakeForm/BasicInfo';
import { AdditionalFieldTypes } from 'enums/onboarding/crm';
import { OptionalAdditionalFieldValidationSchema } from 'validations/onboarding-new/structure';
import {
  STRING_VALIDATION_RULE,
  NULLABLE_STRING_VALIDATION_RULE,
  CRM_NAME_NO_SPECIAL_CHARACTERS_VALIDATION_RULE,
  CRM_NAME_VALIDATION_RULE,
  NULLABLE_NUMBER_VALIDATION_RULE,
} from 'validations/common';

export const entityTypeStepValidationSchema = Yup.object().shape({
  countryOfIncorporation: NULLABLE_STRING_VALIDATION_RULE.required(
    'form:validation_messages.required',
  ),
  entityTypeCategory: NULLABLE_STRING_VALIDATION_RULE.required(
    'server_errors:custom_errors.intake_form_entity_type_required',
  ),
});

export const basicInfoStepValidationRules = Yup.object().shape({
  legalName: CRM_NAME_NO_SPECIAL_CHARACTERS_VALIDATION_RULE.max(100, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 100,
    },
  }),

  tradeName: STRING_VALIDATION_RULE.max(100, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 100,
    },
  }),

  hearTenet: NULLABLE_STRING_VALIDATION_RULE,

  referrerName: STRING_VALIDATION_RULE.max(100, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 100,
    },
  }),

  referrerCompany: STRING_VALIDATION_RULE.max(100, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 100,
    },
  }),

  referrerInformation: STRING_VALIDATION_RULE.max(1000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 1000,
    },
  }),

  relationships: Yup.array()
    .of(
      Yup.object().shape({
        template: NULLABLE_STRING_VALIDATION_RULE,
        additionalFields: Yup.array().of(
          OptionalAdditionalFieldValidationSchema,
        ),
      }),
    )
    .test(
      'unique-templates',
      'form:warnings.unique_relationships',
      function (relationships: any) {
        const relationshipsCopy = [...relationships] as RelationshipModel[];

        if (!relationshipsCopy.length) return true;

        const templates = relationshipsCopy
          .map((rel) => rel.template)
          .filter((t) => t != null && t !== '');

        const uniqueCount = new Set(templates).size;

        if (uniqueCount !== templates.length) {
          return false;
        }

        return true;
      },
    ),
});

export const entityProfileStepValidationSchema = Yup.object().shape({
  registrationNumber: CRM_NAME_VALIDATION_RULE.max(100, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 100,
    },
  }),
  regulationExemption: STRING_VALIDATION_RULE.max(1000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 1000,
    },
  }),
  companyWebsite: STRING_VALIDATION_RULE.matches(WEBSITE_REG, {
    message: {
      key: 'form:validation_messages.invalid_value_with_label',
      values: { label: 'onboarding:applicant_information.website_link' },
    },
  }),

  addresses: Yup.array().of(
    Yup.object().shape({
      city: STRING_VALIDATION_RULE.max(20, {
        key: 'form:validation_messages.max_characters',
        values: {
          number: 20,
        },
      }),

      street: STRING_VALIDATION_RULE.max(50, {
        key: 'form:validation_messages.max_characters',
        values: {
          number: 50,
        },
      }),

      state: STRING_VALIDATION_RULE.max(20, {
        key: 'form:validation_messages.max_characters',
        values: {
          number: 20,
        },
      }),

      postal: STRING_VALIDATION_RULE.max(10, {
        key: 'form:validation_messages.max_characters',
        values: {
          number: 10,
        },
      }),
    }),
  ),
});

export const customersAndProductsValidationSchema = Yup.object().shape({
  businessInfo: CRM_NAME_VALIDATION_RULE.min(150, {
    key: 'form:validation_messages.min_characters',
    values: {
      number: 150,
    },
  }).max(5000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 5000,
    },
  }),
  customerOtherDetails: STRING_VALIDATION_RULE.max(1000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 1000,
    },
  }),
  sourceOfRevenueOtherDetails: STRING_VALIDATION_RULE.max(1000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 1000,
    },
  }),
  projectedRevenueInSixMonthsAmount: NULLABLE_NUMBER_VALIDATION_RULE.min(
    1000000,
    {
      key: 'form:validation_messages.min_number_value',
      values: { number: '1,000,000' },
    },
  ),

  projectedRevenueInOneYearAmount: NULLABLE_NUMBER_VALIDATION_RULE.min(
    1000000,
    {
      key: 'form:validation_messages.min_number_value',
      values: { number: '1,000,000' },
    },
  ),

  projectedRevenueInTwoYearsAmount: NULLABLE_NUMBER_VALIDATION_RULE.min(
    1000000,
    {
      key: 'form:validation_messages.min_number_value',
      values: { number: '1,000,000' },
    },
  ),
});

export const fundingValidationSchema = Yup.object().shape({
  timeframeOfInitialDepositInformation: STRING_VALIDATION_RULE.max(1000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 1000,
    },
  }),

  otherBankAccountsInfo: STRING_VALIDATION_RULE.max(1000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 1000,
    },
  }),

  issuesWithOtherBanks: STRING_VALIDATION_RULE.max(1000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 1000,
    },
  }),

  thirdPartyName: STRING_VALIDATION_RULE.max(100, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 100,
    },
  }),

  thirdPartyRelationship: STRING_VALIDATION_RULE.max(1000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 1000,
    },
  }),

  plansToOpenExternalBankAccountsDetails: STRING_VALIDATION_RULE.max(1000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 1000,
    },
  }),

  sourceOfInitialDepositOtherDetails: STRING_VALIDATION_RULE.max(1000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 1000,
    },
  }),
});

export const accountStepValidationSchema = Yup.object().shape({
  accountInflowsDetails: STRING_VALIDATION_RULE.max(1000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 1000,
    },
  }),
  accountOutflowsDetails: STRING_VALIDATION_RULE.max(1000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 1000,
    },
  }),
});

export const associatedIndividualsStepValidationSchema = Yup.object().shape({
  individuals: Yup.array().of(
    Yup.object().shape({
      fullName: STRING_VALIDATION_RULE.max(100, {
        key: 'form:validation_messages.max_characters',
        values: {
          number: 100,
        },
      }),
      relationship: STRING_VALIDATION_RULE.max(1000, {
        key: 'form:validation_messages.max_characters',
        values: {
          number: 1000,
        },
      }),
    }),
  ),
});

export const complianceManagementStepValidationSchema = Yup.object().shape({
  nameOfPersonOrTeam: STRING_VALIDATION_RULE.max(100, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 100,
    },
  }),

  nameOfProvider: STRING_VALIDATION_RULE.max(100, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 100,
    },
  }),

  serviceProviderWebsite: STRING_VALIDATION_RULE.matches(WEBSITE_REG, {
    message: {
      key: 'form:validation_messages.invalid_value_with_label',
      values: { label: 'onboarding:applicant_information.website_link' },
    },
  }),

  nonApplicableReason: STRING_VALIDATION_RULE.max(1000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 1000,
    },
  }),
});

export const additionalQuestionsStepValidationSchema = Yup.object().shape({
  additionalQuestions: Yup.array().of(
    Yup.object().shape({
      isNotApplicableOrAvailable: Yup.boolean(),
      notAvailableValue: STRING_VALIDATION_RULE.max(1000, {
        key: 'form:validation_messages.max_characters',
        values: {
          number: 1000,
        },
      }),
      value: Yup.mixed().when(['type'], (values, schema): any => {
        switch (values[0]) {
          case AdditionalFieldTypes.Percentage:
            return Yup.number()
              .typeError('form:validation_messages.only_numbers')
              .min(0, {
                key: 'form:validation_messages.min_number_value',
                values: { number: 0 },
              })
              .max(100, {
                key: 'form:validation_messages.max_number_value',
                values: { number: 100 },
              });

          case AdditionalFieldTypes.Form:
          case AdditionalFieldTypes.Document:
            return Yup.array();

          case AdditionalFieldTypes.String:
            return STRING_VALIDATION_RULE;

          default:
            return schema;
        }
      }),
    }),
  ),
});

export const serviceProviderStepValidationSchema = Yup.object().shape({
  requiredServiceProviders: Yup.array().of(
    Yup.object().shape({
      additionalFields: Yup.array().of(OptionalAdditionalFieldValidationSchema),
    }),
  ),
  serviceProviders: Yup.array().of(
    Yup.object().shape({
      additionalFields: Yup.array().of(OptionalAdditionalFieldValidationSchema),
    }),
  ),
});

export const ResolveFieldCommentValidationSchema = Yup.object().shape({
  comment: STRING_VALIDATION_RULE.required(
    'form:validation_messages.required',
  ).max(1000, {
    key: 'form:validation_messages.max_characters',
    values: {
      number: 1000,
    },
  }),
});

export const supplementaryQuestionsValidationSchema = Yup.object().shape({
  additionalFields: Yup.array().of(OptionalAdditionalFieldValidationSchema),
});
